import { useState, useEffect, useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';

import Iconify from 'src/components/iconify';

type CreatableOption = string | { inputValue: string; title: string };

const filter = createFilterOptions<CreatableOption>();

type Props = {
  name: string;
  label?: string;
  placeholder?: string;
  helperText?: React.ReactNode;
  required?: boolean;
  options: string[];
  onCreateOption?: (value: string) => void;
};

export default function RHFCreatableAutocomplete({
  name,
  label,
  placeholder,
  helperText,
  required,
  options: initialOptions,
  onCreateOption,
}: Props) {
  const { control, setValue } = useFormContext();

  const [options, setOptions] = useState<string[]>(initialOptions);

  useEffect(() => {
    setOptions((prev) => Array.from(new Set([...initialOptions, ...prev])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOptions]);

  const handleCreateOption = useCallback(
    (value: string) => {
      setOptions((prev) => (prev.includes(value) ? prev : [...prev, value]));
      onCreateOption?.(value);
    },
    [onCreateOption]
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Autocomplete
          {...field}
          value={field.value || null}
          onChange={(event, newValue) => {
            if (typeof newValue === 'string') {
              setValue(name, newValue, { shouldValidate: true });
            } else if (newValue && 'inputValue' in newValue) {
              handleCreateOption(newValue.inputValue);
              setValue(name, newValue.inputValue, { shouldValidate: true });
            } else if (newValue && 'title' in newValue) {
              setValue(name, newValue.title, { shouldValidate: true });
            } else {
              setValue(name, '', { shouldValidate: true });
            }
          }}
          filterOptions={(currentOptions, params) => {
            const filtered = filter(currentOptions, params);

            const { inputValue } = params;
            const isExisting = currentOptions.some((option) => inputValue === option);
            if (inputValue !== '' && !isExisting) {
              filtered.push({
                inputValue,
                title: `Add "${inputValue}"`,
              });
            }

            return filtered;
          }}
          selectOnFocus
          clearOnBlur
          handleHomeEndKeys
          options={options}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            if ('inputValue' in option) return option.inputValue;
            return option.title;
          }}
          renderOption={(props, option) => {
            const isCreateOption = typeof option !== 'string' && 'inputValue' in option;

            return (
              <Box
                component="li"
                {...props}
                sx={
                  isCreateOption
                    ? { color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }
                    : undefined
                }
              >
                {isCreateOption && <Iconify icon="mingcute:add-line" width={18} />}
                {typeof option === 'string' ? option : option.title}
              </Box>
            );
          }}
          freeSolo
          renderInput={(params) => (
            <TextField
              label={label}
              placeholder={placeholder}
              error={!!error}
              helperText={error ? error?.message : helperText}
              required={required}
              {...params}
            />
          )}
        />
      )}
    />
  );
}
